/* Horizon Pro v3 — visual layer behaviour
 * - Adds [data-hp-v3-scrolled] / [data-hp-v3-scrolled-down] to <html>
 * - Drives footer parallax via --hp-v3-footer-y
 * - All passive listeners, all reduced-motion aware. */

(() => {
  'use strict';

  const html = document.documentElement;
  if (!html.hasAttribute('data-hp-v3')) return;

  let lastY = 0;
  let raf = 0;
  const SCROLL_THRESHOLD = 8;
  const SCROLLED_AT = 32;
  const stickyMode = html.getAttribute('data-hp-v3-sticky') || 'none';

  function tick() {
    raf = 0;
    const y = window.scrollY || 0;

    if (y > SCROLLED_AT && !html.hasAttribute('data-hp-v3-scrolled')) {
      html.setAttribute('data-hp-v3-scrolled', '');
    } else if (y <= SCROLLED_AT && html.hasAttribute('data-hp-v3-scrolled')) {
      html.removeAttribute('data-hp-v3-scrolled');
    }

    if (stickyMode === 'on-scroll-up') {
      const dy = y - lastY;
      if (Math.abs(dy) > SCROLL_THRESHOLD) {
        if (dy > 0 && y > 120) {
          html.setAttribute('data-hp-v3-scrolled-down', '');
        } else {
          html.removeAttribute('data-hp-v3-scrolled-down');
        }
      }
    }
    lastY = y;
  }

  function onScroll() {
    if (raf) return;
    raf = requestAnimationFrame(tick);
  }

  tick();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', onScroll);
  document.addEventListener('shopify:section:unload', onScroll);

  /* ---------- Footer height -> CSS var ----------
     The footer is position:absolute (Concept's reveal trick) so it's out of
     flow. To keep the page tall enough that you can scroll past main and see
     the full footer, the page container reserves the footer's height. */
  const footerWrap = document.querySelector('.hp-v3-page-container > .hp-v3-footer-wrap');
  if (footerWrap) {
    const main = document.querySelector('main.content-for-layout');
    let footerAdjacentSection = null;
    let footerRaf = 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const updateFooterAdjacentSection = () => {
      const sections = document.querySelectorAll('main.content-for-layout > .shopify-section');
      const lastSection = sections[sections.length - 1] || null;

      if (footerAdjacentSection && footerAdjacentSection !== lastSection) {
        footerAdjacentSection.classList.remove('hp-v3-footer-adjacent');
      }

      footerAdjacentSection = lastSection;
      footerAdjacentSection?.classList.add('hp-v3-footer-adjacent');
    };

    const updateFooterHeight = () => {
      const h = footerWrap.offsetHeight;
      html.style.setProperty('--hp-v3-footer-h', `${h}px`);
    };

    const updateFooterReveal = () => {
      footerRaf = 0;

      if (!main || !html.hasAttribute('data-hp-v3-footer-parallax') || reduceMotion.matches || window.innerWidth < 768) {
        html.style.setProperty('--hp-v3-footer-y', '0%');
        html.removeAttribute('data-hp-v3-footer-revealing');
        return;
      }

      const mainRect = main.getBoundingClientRect();
      const footerHeight = Math.max(footerWrap.offsetHeight, 1);
      const gap = parseFloat(getComputedStyle(html).getPropertyValue('--hp-v3-footer-reveal-gap')) || 0;
      const revealStart = window.innerHeight - gap;
      const progress = Math.max(0, Math.min(1, (revealStart - mainRect.bottom) / footerHeight));
      const y = -50 + (progress * 50);

      html.toggleAttribute('data-hp-v3-footer-revealing', mainRect.bottom <= revealStart + 2);
      html.style.setProperty('--hp-v3-footer-y', `${y.toFixed(3)}%`);
    };

    const scheduleFooterReveal = () => {
      if (footerRaf) return;
      footerRaf = requestAnimationFrame(updateFooterReveal);
    };

    updateFooterAdjacentSection();
    updateFooterHeight();
    updateFooterReveal();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => requestAnimationFrame(() => {
        updateFooterHeight();
        updateFooterReveal();
      }));
      ro.observe(footerWrap);
    } else {
      window.addEventListener('resize', updateFooterHeight, { passive: true });
    }

    window.addEventListener('scroll', scheduleFooterReveal, { passive: true });
    window.addEventListener('resize', scheduleFooterReveal, { passive: true });
    reduceMotion.addEventListener?.('change', scheduleFooterReveal);

    const refreshFooterReveal = () => requestAnimationFrame(() => {
      updateFooterAdjacentSection();
      updateFooterHeight();
      updateFooterReveal();
    });

    document.addEventListener('shopify:section:load', refreshFooterReveal);
    document.addEventListener('shopify:section:unload', refreshFooterReveal);
  }
})();
